import {
    DATE_FORMAT,
    DATE_FORMAT_SHORTEN,
    DATE_WITHOUT_YEAR_FORMAT,
    GuestStatus,
    IReservation,
    IReservationsDateTimeGroup,
    IReservationsShiftGroupWithGuestsNumber,
    IRestaurantConfiguration,
    Label,
    ReservationStatus,
    ReservationUtils,
    StringUtils,
    TIME_FORMAT,
    TLanguageKey,
} from '@localina/core';
import { AddCircleIcon, PeopleIcon } from '@localina/icons';
import { DateTime } from 'luxon';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHaveAccountFeatures } from '../../api/queries/account';
import { IReservationForm } from '../../schemas/interfaces';
import { getTablesNamesByIds } from '../../utils/TablePlanService';
import { GuestStatusIcon } from '../GuestStatusIcon';
import { ReservationGuestAdditionalInfo } from '../ReservationGuestAdditionalInfo';
import ReservationOriginIcon from '../ReservationOriginIcon/ReservationOriginIcon';
import ReservationStatusActionIcon from '../ReservationStatus/ReservationStatusActionIcon';
import { ReservationTableNumbers } from '../ReservationTableNumbers';
import { ReservationAllergyLine } from '../ReservationAllergyLine';
import ReservationAreaCodes from './ReservationAreaCodes';
import { ReservationCompactRow } from './ReservationCompactRow';
import { ReservationNoteLine } from './ReservationNoteLine';

interface IProps extends IReservationsShiftGroupWithGuestsNumber {
    restaurantConfiguration: IRestaurantConfiguration;
    onCreate: (defaultValues?: Partial<IReservationForm>) => void;
    // Display name when it differs from the configured one (e.g. disambiguated by area).
    label?: string;
    onEdit: (reservation: IReservation) => void;
    unassignedShift?: boolean;
    isToday?: boolean;
    nowISO?: string;
    nowLineBeforeDatetime?: string;
    nowLineVisible?: boolean;
    // only the group in the visible swipe tab may scroll, otherwise scrollIntoView also scrolls
    // the swipe container horizontally, which SwipeableViews treats as a tab change
    nowLineScrollIntoView?: boolean;
}

// layoutReady: the group's width is measured; before that the rows render in the taller phone
// layout, and scrolling then would land the line far off once they shrink
const NowLine: React.FC<{ label: string; scrollIntoView?: boolean; layoutReady: boolean }> = ({
    label,
    scrollIntoView,
    layoutReady,
}) => {
    const ref = React.useRef<HTMLDivElement>(null);
    // decided when the line appears, scrolled once the layout is final
    const pendingScroll = React.useRef(scrollIntoView);
    React.useEffect(() => {
        if (layoutReady && pendingScroll.current) {
            pendingScroll.current = false;
            ref.current?.scrollIntoView({ block: 'center' });
        }
    }, [layoutReady]);
    return (
        <div ref={ref} className="reservations-shiftgroup__now-line">
            <Label type="text" value={label} extraClassName="reservations-shiftgroup__now-line__label" />
        </div>
    );
};

// below this group width the row switches to the stacked phone layout (ReservationCompactRow)
const COMPACT_MAX_WIDTH = 700;

const formatCreatedAt = (iso: string) => {
    const created = DateTime.fromISO(iso);
    return created.year === DateTime.now().year
        ? created.toFormat(DATE_WITHOUT_YEAR_FORMAT)
        : created.toFormat(DATE_FORMAT_SHORTEN);
};

const ReservationsShiftGroup: React.FC<IProps> = (props) => {
    const { i18n, t } = useTranslation();

    const { width, ref: shiftgroupWrapperRef } = useObserveDivWidth();

    const mediaQueryClassName = useMemo(() => {
        let reservationsSize = '';
        if (width > 1200) {
            reservationsSize = 'extra-large';
        } else if (width > 1000) {
            reservationsSize = 'large';
        } else if (width > COMPACT_MAX_WIDTH) {
            reservationsSize = 'medium';
        } else {
            reservationsSize = 'compact';
        }

        let reservationCommentsDisplayed = '';
        if (width > 1015) {
            reservationCommentsDisplayed = 'display-comments';
            if (width < 1172) {
                reservationCommentsDisplayed += ' dense-table';
            }
        }

        return StringUtils.combineStrings([reservationsSize, reservationCommentsDisplayed]);
    }, [width]);

    const isCompact = width <= COMPACT_MAX_WIDTH;

    const [reservationsGroups, setReservationsGroups] = React.useState<IReservationsDateTimeGroup[]>([]);

    const [canUseTablePlans, canUseColorIndicator, canUseAreas] = useHaveAccountFeatures([
        'tablePlans',
        'reservationColor',
        'areas',
    ]);

    React.useEffect(() => {
        if (props.reservations) {
            setReservationsGroups(ReservationUtils.groupReservationsForDateTime(props.reservations));
        }
    }, [props.reservations]);

    const handleCreateReservation = (datetime: string) => () => {
        props.onCreate({
            date: [DateTime.fromISO(datetime).toFormat(DATE_FORMAT)],
            time: DateTime.fromISO(datetime).toFormat(TIME_FORMAT),
        });
    };

    const handleEditReservation = (reservation: IReservation) => () => {
        props.onEdit(reservation);
    };

    const shiftNotAssignedOrBad = props.unassignedShift || !props.shift;

    // with the now line on, the header tells how many of the shift's guests are still to come
    const upcomingGuests =
        props.nowLineVisible && props.nowISO !== undefined
            ? ReservationUtils.sumTotalParticipantsOfReservations(
                  props.reservations.filter(
                      (reservation) =>
                          DateTime.fromISO(reservation.reservationDateTime) >= DateTime.fromISO(props.nowISO!),
                  ),
              )
            : undefined;

    return (
        <div
            ref={shiftgroupWrapperRef}
            className={StringUtils.combineStrings([
                mediaQueryClassName,
                'reservations-shiftgroup',
                shiftNotAssignedOrBad && 'unassigned-shift',
                !reservationsGroups.length && 'display-none',
            ])}
        >
            <div className="reservations-shiftgroup__header">
                <div className="reservations-shiftgroup__header__shift">
                    <Label
                        type="title"
                        value={
                            props.shift
                                ? props.label ?? props.shift.name[i18n.language as TLanguageKey]
                                : t('reservations.group.unknown')
                        }
                        variant="body2"
                    />
                    {}
                    {Boolean(props.shift) && (
                        <Label
                            type="info"
                            extraClassName="reservations-shiftgroup__header__hours"
                            value={`${props.shift!.from}\u2009\u2013\u2009${props.shift!.to}`}
                        />
                    )}
                </div>
                {}
                <div className="reservations-shiftgroup__header__info">
                    <Label
                        type="info"
                        value={
                            upcomingGuests === undefined
                                ? t('reservations.view.countOfGuestsAtExactTime.label', { count: props.guests })
                                : t('reservations.view.countOfUpcomingGuests.label', {
                                      count: props.guests,
                                      upcoming: upcomingGuests,
                                  })
                        }
                    />
                </div>
            </div>
            <div className="reservations-shiftgroup__body">
                {reservationsGroups.map((reservationsGroup) => {
                    const timeGroupPeopleCount = ReservationUtils.sumTotalParticipantsOfReservations(
                        reservationsGroup.reservations,
                    );
                    return (
                        <React.Fragment key={reservationsGroup.datetime}>
                            {props.isToday && props.nowLineBeforeDatetime === reservationsGroup.datetime && (
                                <NowLine
                                    label={t('reservations.view.now')}
                                    scrollIntoView={props.nowLineScrollIntoView}
                                    layoutReady={width > 0}
                                />
                            )}
                            <div className="reservations-shiftgroup__time-group">
                                <div className="reservations-shiftgroup__time-group__header">
                                    <Label
                                        type="text"
                                        extraClassName="reservations-shiftgroup__time-group__header__time"
                                        value={DateTime.fromISO(reservationsGroup.datetime, { zone: 'utc' })
                                            .toLocal()
                                            .toFormat(TIME_FORMAT)}
                                    />
                                    {timeGroupPeopleCount > 0 && (
                                        <Label
                                            type={'info'}
                                            value={t('reservations.view.countOfGuestsAtExactTime.label', {
                                                count: timeGroupPeopleCount,
                                            })}
                                        />
                                    )}
                                    {DateTime.local() <
                                        DateTime.fromISO(reservationsGroup.datetime, { zone: 'utc' }).toLocal() && (
                                        <div className="reservations-shiftgroup__time-group__header__action">
                                            <AddCircleIcon
                                                onClick={handleCreateReservation(reservationsGroup.datetime)}
                                            />
                                        </div>
                                    )}
                                </div>
                                <div className="reservations-shiftgroup__time-group__body">
                                    {reservationsGroup.reservations.map((reservation) => (
                                        <div
                                            key={reservation.id}
                                            className={StringUtils.combineStrings([
                                                'reservations-shiftgroup__time-group__reservation',
                                                props.nowLineVisible &&
                                                    props.nowISO !== undefined &&
                                                    DateTime.fromISO(reservation.reservationDateTime) <
                                                        DateTime.fromISO(props.nowISO) &&
                                                    'reservations-shiftgroup__time-group__reservation--past',
                                                ReservationUtils.isReservationInDeadStatus(reservation) &&
                                                    'reservations-shiftgroup__time-group__cancelled',
                                                reservation.status === ReservationStatus.APPROVAL_PENDING &&
                                                    'reservations-shiftgroup__time-group__requested',
                                                reservation.guestInfo?.guestStatus === GuestStatus.VIP && 'vip',
                                                reservation.guestInfo?.guestStatus === GuestStatus.BLACKLIST &&
                                                    'blacklist',
                                                !reservation.guestInfo && 'walkin',
                                                Boolean(reservation.guestInfo?.allergies) && 'has-allergy',
                                            ])}
                                            onClick={handleEditReservation(reservation)}
                                        >
                                            {Boolean(canUseColorIndicator && reservation.color) && (
                                                <div
                                                    className={'reservations-shiftgroup__time-group__color-indicator'}
                                                    style={{ backgroundColor: reservation.color! }}
                                                />
                                            )}
                                            {isCompact ? (
                                                <ReservationCompactRow
                                                    reservation={reservation}
                                                    restaurantConfiguration={props.restaurantConfiguration}
                                                    canUseTablePlans={canUseTablePlans}
                                                    canUseAreas={canUseAreas}
                                                    createdAtLabel={
                                                        reservation.auditInfo?.createdAt
                                                            ? formatCreatedAt(reservation.auditInfo.createdAt)
                                                            : undefined
                                                    }
                                                />
                                            ) : (
                                                <>
                                                    <div className="reservation__guest">
                                                        <div className="reservation__guest__guest-status">
                                                            <GuestStatusIcon
                                                                guestStatus={reservation.guestInfo?.guestStatus}
                                                            />
                                                        </div>
                                                        <div className="reservation__guest__name-wrapper">
                                                            <Label
                                                                type="text"
                                                                extraClassName="reservation__guest__name"
                                                                value={
                                                                    reservation.guestInfo
                                                                        ? reservation.guestInfo.lastName
                                                                        : t('reservations.view.fields.walkin')
                                                                }
                                                            />
                                                            {}
                                                            {Boolean(
                                                                reservation.guestInfo?.firstName ||
                                                                    reservation.guestInfo?.company,
                                                            ) && (
                                                                <div className="reservation__guest__meta">
                                                                    {Boolean(reservation.guestInfo?.firstName) && (
                                                                        <span className="reservation__guest__meta__item">
                                                                            {reservation.guestInfo!.firstName}
                                                                        </span>
                                                                    )}
                                                                    {Boolean(reservation.guestInfo?.company) && (
                                                                        <span className="reservation__guest__meta__item">
                                                                            {reservation.guestInfo!.company}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    {}
                                                    <div className="reservation__notes">
                                                        {}
                                                        <div className="reservation__notes__icons">
                                                            <div className="reservation__info__icon notes-fallback">
                                                                {Boolean(
                                                                    reservation.comment ||
                                                                        reservation.guestInfo?.restaurantComment,
                                                                ) && (
                                                                    <ReservationGuestAdditionalInfo
                                                                        reservation={reservation}
                                                                    />
                                                                )}
                                                            </div>
                                                        </div>
                                                        {}
                                                        <div className="reservation__notes__text">
                                                            {Boolean(reservation.guestInfo?.allergies) && (
                                                                <ReservationAllergyLine
                                                                    allergies={reservation.guestInfo!.allergies!}
                                                                />
                                                            )}
                                                            {Boolean(reservation.comment) && (
                                                                <ReservationNoteLine
                                                                    kind="remark"
                                                                    text={reservation.comment!}
                                                                />
                                                            )}
                                                            {Boolean(reservation.guestInfo?.restaurantComment) && (
                                                                <ReservationNoteLine
                                                                    kind="guest"
                                                                    text={reservation.guestInfo!.restaurantComment!}
                                                                />
                                                            )}
                                                        </div>
                                                    </div>
                                                    {}
                                                    <div className="reservation__info">
                                                        <div className="reservation__participants">
                                                            <PeopleIcon />
                                                            <Label type="text" value={reservation.participants} />
                                                        </div>
                                                    </div>
                                                    {}
                                                    {(canUseTablePlans || canUseAreas) && (
                                                        <div className="tables-and-area-codes-container">
                                                            {canUseTablePlans && (
                                                                <div className="reservation__tablenumber">
                                                                    {reservation.tableIds && (
                                                                        <ReservationTableNumbers
                                                                            tableNumbers={getTablesNamesByIds(
                                                                                reservation.tableIds,
                                                                                props.restaurantConfiguration
                                                                                    .virtualAreas,
                                                                            )}
                                                                        />
                                                                    )}
                                                                </div>
                                                            )}
                                                            {canUseAreas && (
                                                                <ReservationAreaCodes reservation={reservation} />
                                                            )}
                                                        </div>
                                                    )}
                                                    <div className={'area-code-and-status-wrapper'}>
                                                        <ReservationStatusActionIcon
                                                            reservation={reservation}
                                                            className="reservation-status-box"
                                                        />
                                                    </div>
                                                    {Boolean(reservation.auditInfo) && (
                                                        <div className="date-created-and-type d-xs-none">
                                                            {Boolean(reservation.auditInfo!.createdAt) && (
                                                                <Label
                                                                    type="info"
                                                                    value={formatCreatedAt(
                                                                        reservation.auditInfo!.createdAt,
                                                                    )}
                                                                />
                                                            )}
                                                            <div className="reservation-origin-wrapper">
                                                                <ReservationOriginIcon reservation={reservation} />
                                                            </div>
                                                        </div>
                                                    )}
                                                    {Boolean(reservation.guestInfo?.allergies) && (
                                                        <ReservationAllergyLine
                                                            allergies={reservation.guestInfo!.allergies!}
                                                            className="reservation__allergy-band"
                                                        />
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </React.Fragment>
                    );
                })}
            </div>
        </div>
    );
};

function useObserveDivWidth<T extends HTMLDivElement>() {
    const [width, setWidth] = React.useState(0);
    const ref = React.useRef<T>(null);

    React.useEffect(() => {
        const observer = new ResizeObserver((entries) => {
            setWidth(entries[0].contentRect.width);
        });

        if (ref.current) {
            observer.observe(ref.current);
        }

        return () => {
            ref.current && observer.unobserve(ref.current);
        };
    }, []);

    return {
        width,
        ref,
    };
}

export default ReservationsShiftGroup;
